/**
 * Apres la vente (design 4.4) : « Vente S03-P1-000123 enregistrée sur ce
 * poste », la monnaie a rendre encore en gros, le recu qui s'imprime tout
 * seul (une fois), « Réimprimer le reçu » et « Nouvelle vente ».
 *
 * Chaque impression est notee sur la vente (receipt_print_count + 1) ; une
 * reimpression porte la mention « COPIE ».
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import { palette } from '../app/theme';
import RecuImprimable from '../print/RecuImprimable';
import { chargerContexteRecu } from '../print/contexteRecu';
import { imprimerRecu } from '../print/imprimer';
import { marquerRecuImprime } from '../store/sales';
import { formatMontant } from '../vente/contrat';

const CARD_SX = {
    bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: '14px',
    boxShadow: '0 1px 3px rgba(15,23,42,.06)', p: 3,
};

export default function EcranRecu({ db, vente: venteInitiale, onNouvelleVente, imprimer = imprimerRecu }) {
    const [vente, setVente] = useState(venteInitiale);
    const [magasin, setMagasin] = useState(null);
    const [erreur, setErreur] = useState(null);
    const autoImprime = useRef(false);
    const enCours = useRef(false);

    useEffect(() => {
        let cancelled = false;
        chargerContexteRecu(db)
            .then((ctx) => { if (!cancelled) setMagasin(ctx); })
            .catch(() => { if (!cancelled) setMagasin({ store: {}, config: {} }); });
        return () => { cancelled = true; };
    }, [db]);

    const imprimerEtNoter = useCallback(async () => {
        if (enCours.current) return;
        enCours.current = true;
        try {
            imprimer();
            setVente(await marquerRecuImprime(db, vente.sale_uuid));
            setErreur(null);
        } catch (_error) {
            setErreur('Le reçu n\'a pas pu être imprimé. Vérifiez l\'imprimante, puis « Réimprimer le reçu ».');
        } finally {
            enCours.current = false;
        }
    }, [db, imprimer, vente.sale_uuid]);

    // Premiere impression, automatique, quand le recu est dans la page.
    useEffect(() => {
        if (!magasin || autoImprime.current || vente.receipt_print_count > 0) return;
        autoImprime.current = true;
        imprimerEtNoter();
    }, [magasin, vente.receipt_print_count, imprimerEtNoter]);

    const p = vente.payment;
    return (
        <Box data-testid="ecran-recu" sx={{ flex: 1, display: 'flex', gap: 2, p: 2, minHeight: 0 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box role="status" sx={{ ...CARD_SX, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: palette.successTint, color: palette.successOnTint }}>
                    <CheckCircleIcon aria-hidden="true" sx={{ fontSize: 40 }} />
                    <Typography data-testid="vente-enregistree" sx={{ fontSize: 26, fontWeight: 800 }}>
                        Vente {vente.local_number} enregistrée sur ce poste
                    </Typography>
                </Box>
                <Box data-testid="monnaie" data-cents={p.change_given_cents} sx={{ ...CARD_SX, bgcolor: palette.successTint, border: `2px solid ${palette.success}` }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: palette.successOnTint, mb: 1 }}>MONNAIE À RENDRE</Typography>
                    <Typography sx={{ fontSize: 56, fontWeight: 800, lineHeight: 1.1, color: palette.successOnTint }}>
                        {formatMontant(p.change_given_cents)}
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: 18, color: palette.successOnTint }}>
                        Reçu {formatMontant(p.cash_received_cents)} · à payer {formatMontant(p.amount_due_cents)}
                    </Typography>
                </Box>
                {erreur && (
                    <Typography role="alert" sx={{ fontSize: 18, fontWeight: 600, color: palette.errorOnTint, bgcolor: palette.errorTint, p: 1.5, borderRadius: 2 }}>
                        {erreur}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    disabled={!magasin}
                    onClick={imprimerEtNoter}
                    sx={{ minHeight: 64, fontSize: 22, borderRadius: '12px', borderWidth: 2, color: palette.text, borderColor: palette.border, '&:hover': { borderWidth: 2 } }}
                >
                    Réimprimer le reçu
                </Button>
                <Button
                    variant="contained"
                    autoFocus
                    onClick={onNouvelleVente}
                    sx={{ minHeight: 88, fontSize: 26, fontWeight: 800, borderRadius: '12px' }}
                >
                    NOUVELLE VENTE
                </Button>
            </Box>
            <Box sx={{ width: 420, flex: '0 0 auto', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
                {magasin && <RecuImprimable vente={vente} magasin={magasin} />}
            </Box>
        </Box>
    );
}
